import React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { HelpCircle, ArrowRight } from 'lucide-react';
import { helpContent } from '../../data/helpContent';

interface HelpTooltipProps {
  contentKey?: string;
  content?: string;
  title?: string;
  /**
   * Optional Help Center anchor (e.g. "crs", "poverty"). When set, the
   * tooltip footer renders a "Learn more" link to /help#<anchor>.
   * Falls back to the contentKey's entry anchor if not provided.
   */
  anchor?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  iconSize?: 'sm' | 'md';
  className?: string;
}

const HelpTooltip: React.FC<HelpTooltipProps> = ({
  contentKey,
  content,
  title,
  anchor,
  side = 'top',
  align = 'center',
  iconSize = 'sm',
  className = '',
}) => {
  let tooltipTitle = title;
  let tooltipDescription = content;
  let tooltipAnchor = anchor;

  if (contentKey && helpContent[contentKey]) {
    const entry = helpContent[contentKey];
    tooltipTitle = tooltipTitle || entry.title;
    tooltipDescription = tooltipDescription || entry.description;
    tooltipAnchor = tooltipAnchor || entry.anchor;
  }

  if (!tooltipDescription) return null;

  const iconPx = iconSize === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>
        <button
          type="button"
          className={`ra-help-trigger inline-flex items-center justify-center rounded-full p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50 ${className}`}
          aria-label={tooltipTitle ? `Help: ${tooltipTitle}` : 'Help'}
        >
          <HelpCircle className={`${iconPx} ra-help-icon`} aria-hidden="true" />
        </button>
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          className="ra-help-tooltip"
          side={side}
          align={align}
          sideOffset={6}
          collisionPadding={12}
        >
          {tooltipTitle && (
            <span className="ra-help-tooltip-title">{tooltipTitle}</span>
          )}
          <span className="ra-help-tooltip-body">{tooltipDescription}</span>
          {tooltipAnchor && (
            <a
              href={`/help#${tooltipAnchor}`}
              className="ra-help-tooltip-link"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                marginTop: 8,
                color: '#60a5fa',
                fontSize: '0.78rem',
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Learn more
              <ArrowRight size={12} aria-hidden="true" />
            </a>
          )}
          <TooltipPrimitive.Arrow className="ra-help-tooltip-arrow" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
};

export default HelpTooltip;
